import { type UseInfiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import qs from 'query-string';
import {
  API_ENDPOINT,
  type ClientApiV1CourseUserUserIdQueryString,
  type ClientApiV1CourseUserUserIdResponse,
  fetchClientApiV1CourseUserUserId,
  useClientApiV1CourseUserUserIdQuery,
} from '../../resources/@types/courseSchema';

interface UseClientV1CourseUserUserIdQuery {
  userId: string;
  queryObject?: Partial<ClientApiV1CourseUserUserIdQueryString>;
  options?: Parameters<typeof useClientApiV1CourseUserUserIdQuery>[0]['options'];
}

const DEFAULT_QUERY_STRING = {
  pageSize: 12,
  pageNumber: 1,
  sort: 'RECENT',
};

export function useCourseUserUserIdQuery({
  userId,
  options,
  queryObject,
}: UseClientV1CourseUserUserIdQuery) {
  return useClientApiV1CourseUserUserIdQuery({
    queryKey: getUseCourseUserUserIdQueryQueryKey({ userId }),
    url: getUseCourseUserUserIdQueryQueryUrl({ userId, ...queryObject }),
    options,
  });
}

export const getUseCourseUserUserIdQueryQueryKey = ({
  userId,
}: {
  userId: string;
} & ClientApiV1CourseUserUserIdQueryString) => [
  API_ENDPOINT.CLIENT_V1_COURSE_USER_USERID(userId),
  { ...DEFAULT_QUERY_STRING },
];

export const getUseCourseUserUserIdQueryQueryUrl = ({
  userId,
  ...queryObject
}: { userId: string } & ClientApiV1CourseUserUserIdQueryString) =>
  qs.stringifyUrl({
    url: API_ENDPOINT.CLIENT_V1_COURSE_USER_USERID(userId),
    query: {
      ...DEFAULT_QUERY_STRING,
      ...queryObject,
    },
  });

/**
 * ------- 무한 스크롤 쿼리 Hook -------
 */

export const getInstructorIdCourseInfinityQueryKey = ({
  userId,
  ...queryObject
}: {
  userId: string;
} & ClientApiV1CourseUserUserIdQueryString) => [
  API_ENDPOINT.CLIENT_V1_COURSE_USER_USERID(userId),
  { ...DEFAULT_QUERY_STRING, ...queryObject },
  'infinity',
];

export function useInstructorIdCourseInfinityQuery({
  userId,
  queryObject,
  options,
}: {
  userId: string;
  queryObject?: Partial<ClientApiV1CourseUserUserIdQueryString>;
  options?: Omit<
    UseInfiniteQueryOptions<ClientApiV1CourseUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
}) {
  const result = useInfiniteQuery<ClientApiV1CourseUserUserIdResponse>(
    getInstructorIdCourseInfinityQueryKey({
      userId,
      ...queryObject,
    }),
    ({ pageParam = 1 }) =>
      fetchClientApiV1CourseUserUserId(
        getUseCourseUserUserIdQueryQueryUrl({
          userId,
          pageNumber: Number(pageParam),
          ...queryObject,
        }),
      ),
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.data) {
          return undefined;
        }

        if (lastPage.data.pageNumber >= lastPage.data.totalPage) {
          return undefined;
        }

        return lastPage.data.pageNumber + 1;
      },
      ...options,
    },
  );

  const data = result.data?.pages?.map((page) => page?.data) ?? [];

  return { ...result, data };
}
